import React, { useState } from 'react';
import { FaFileInvoice, FaSpinner } from 'react-icons/fa';
import TenantRow from './_tenant_row';
import { Button, Form, InputGroup, Placeholder, Modal } from 'react-bootstrap';
import { useAddTypeCInvoicingMutation, useGetTYPECInvoicesQuery } from '../../lib/apiSlice/invoicing/invoicingSlice.tsx';
import { toast } from 'react-toastify';

const TenantsTable = ({
  loading, handleSelectTenant, selectedTenants,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [apartmentFilter, setApartmentFilter] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [failedInvoices, setFailedInvoices] = useState([]);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [generationSummary, setGenerationSummary] = useState(null);
  const [sendSms, setSendSms] = useState(true);

  const itemsPerPage = 10;
  const { data: tenantsDataInvoice, isLoading } = useGetTYPECInvoicesQuery();

  // Get unique apartment names for filter dropdown
  const apartmentOptions = tenantsDataInvoice
    ? Array.from(
      new Set(
        tenantsDataInvoice
          .map(t => t.apartment_name)
          .filter(Boolean)
      )
    )
    : [];

  // Filter tenants based on search term and apartment filter
  const filteredTenants = tenantsDataInvoice
    ? tenantsDataInvoice.filter(tenant => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        tenant.user_name.toLowerCase().includes(searchLower) ||
        (tenant.user_phone_number && tenant.user_phone_number.includes(searchTerm)) ||
        (tenant.house_number && tenant.house_number.toLowerCase().includes(searchLower)) ||
        (tenant.apartment_name && tenant.apartment_name.toLowerCase().includes(searchLower)) ||
        (tenant.kra_pin && tenant.kra_pin.toString().includes(searchTerm));
      const matchesApartment =
        !apartmentFilter || tenant.apartment_name === apartmentFilter;
      return matchesSearch && matchesApartment;
    })
    : [];

  // Calculate pagination with filtered tenants
  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTenants.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled immediately via the searchTerm state
  };
  
  const generateInvoices = () => {
    if (selectedTenants.length === 0) {
      toast.error('Please select at least one tenant');
      return;
    }
    setShowConfirmModal(true);
  };

  const [addInvoice] = useAddTypeCInvoicingMutation();

  const extractErrorMessage = (err, fallbackMessage) => {
    if (typeof err?.data === 'string') {
      return err.data;
    }
    return err?.data?.message || err?.data?.error || fallbackMessage;
  };

  const buildFailedInvoiceList = (attemptedInvoices = [], failedEntries = []) => {
    const attemptedInvoicesMap = new Map(
      attemptedInvoices.map((invoice) => [invoice.invoice_id, invoice])
    );

    return failedEntries.map((failedEntry, index) => ({
      ...(attemptedInvoicesMap.get(failedEntry.invoice_id) || {}),
      ...failedEntry,
      rowKey: `${failedEntry.invoice_id || 'missing'}-${index}`,
    }));
  };

  const applyGenerationSummary = (response, attemptedInvoices) => {
    const createdCount = response.created_count ?? response.created?.length ?? 0;
    const skippedCount = response.skipped_count ?? response.skipped_existing?.length ?? 0;
    const failedEntries = response.failed || [];
    const failedList = buildFailedInvoiceList(attemptedInvoices, failedEntries);
    const failedCount = response.failed_count ?? failedList.length;

    setGenerationSummary({
      requestedCount: response.requested_count ?? attemptedInvoices.length,
      processedCount: response.processed_count ?? (createdCount + skippedCount + failedCount),
      createdCount,
      skippedCount,
      failedCount,
      message: response.message,
      notificationError: response.notification_error,
    });
    setFailedInvoices(failedList);
    setShowFailedModal(failedCount > 0);

    if (response.notification_error) {
      toast.warning('Invoice generation finished, but the failure report email could not be sent.');
    }

    return { createdCount, skippedCount, failedCount };
  };

  const handleConfirmGenerate = async () => {
    const selected = tenantsDataInvoice?.filter(tenant =>
      selectedTenants.includes(tenant.tenant_id)
    );

    if (!selected || selected.length === 0) {
      toast.warning("No tenants selected for invoice generation.");
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await addInvoice({ invoices: selected, send_sms: sendSms }).unwrap();
      const { createdCount, skippedCount, failedCount } = applyGenerationSummary(response, selected);

      const notify = failedCount > 0 ? toast.warning : toast.success;
      notify(
        `Generated ${createdCount} eTIMS invoice(s). ${skippedCount} already existed. ${failedCount} failed.`
      );
    } catch (err) {
      console.error("Invoice generation failed:", err);
      toast.error(extractErrorMessage(err, "An error occurred while generating the invoices."));
    } finally {
      setIsGenerating(false);
      setShowConfirmModal(false);
    }
  };

  const handleRetryFailed = async () => {
    if (failedInvoices.length === 0) {
      return;
    }

    setIsGenerating(true);
    try {
      const response = await addInvoice({ invoices: failedInvoices, send_sms: sendSms }).unwrap();
      const { createdCount, skippedCount, failedCount } = applyGenerationSummary(response, failedInvoices);

      const notify = failedCount > 0 ? toast.warning : toast.success;
      notify(
        `Generated ${createdCount} eTIMS invoice(s). ${skippedCount} already existed. ${failedCount} failed.`
      );

      if (failedCount === 0) {
        setFailedInvoices([]);
        setShowFailedModal(false);
        setGenerationSummary(null);
      }
    } catch (err) {
      console.error("Retry failed:", err);
      toast.error(extractErrorMessage(err, "An error occurred while retrying failed invoices."));
    } finally {
      setIsGenerating(false);
    }
  };

  const SkeletonRow = () => (
    <tr>
      {[...Array(11)].map((_, idx) => (
        <td key={idx} className="px-4 py-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={12} className="rounded w-full h-4" />
          </Placeholder>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="bg-white shadow border border-gray-200 rounded-lg">
      <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-4 px-6 py-4 border-gray-100 border-b">
        <h5 className="mb-0 font-semibold text-gray-800 text-xl">Category C Tenants</h5>
        <div className="flex md:flex-row flex-col md:items-center gap-2">
          {/* Apartment filter select */}
          <select
            className="focus:ring-opacity-50 px-3 py-2 border-gray-300 focus:border-blue-400 rounded-md focus:ring focus:ring-blue-200"
            value={apartmentFilter}
            onChange={e => {
              setApartmentFilter(e.target.value);
              setCurrentPage(1);
            }}
            disabled={loading}
          >
            <option value="">All Properties</option>
            {apartmentOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <Form onSubmit={handleSearch} className="w-full md:w-auto">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={loading}
                className="focus:ring-opacity-50 border-gray-300 focus:border-blue-400 rounded-md focus:ring focus:ring-blue-200"
              />
            </InputGroup>
          </Form>
          <Form.Check
            type="switch"
            id="send-sms-typec"
            label="Send SMS"
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
            className="ms-2"
          />
          <button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 shadow px-4 py-2 rounded-md text-white transition"
            onClick={generateInvoices}
            disabled={selectedTenants.length === 0 || isGenerating}
          >
            {isGenerating ? (
              <>
                <FaSpinner className="me-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FaFileInvoice className="me-2" />
                Generate Invoices ({selectedTenants.length})
              </>
            )}
          </button>
        </div>
      </div>
      <div className="px-0 py-4">
        <div className="border border-gray-100 rounded-lg overflow-x-auto">
          <table className="divide-y divide-gray-200 min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th width="50px" className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">
                </th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Tenant Details</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Unit</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Pin Number</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Invoice Details</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Taxable Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Invoice Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Total Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Invoice Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading || isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : currentItems.length > 0 ? (
                currentItems.map(tenant => (
                  <TenantRow
                    key={tenant.tenant_id}
                    tenant={tenant}
                    selected={selectedTenants.includes(tenant.tenant_id)}
                    onSelect={handleSelectTenant}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="py-6 text-gray-400 text-center">
                    {searchTerm ? 'No tenants found matching your search' : 'No tenants available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !isLoading && filteredTenants.length > itemsPerPage && (
          <div className="flex md:flex-row flex-col justify-between items-center gap-2 mt-6">
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="text-gray-600 text-sm">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTenants.length)} of {filteredTenants.length}
            </span>
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-blue-600 text-white">
          <Modal.Title>
            Confirm Invoice Generation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-5 text-center">
          <div className="mx-auto mb-3 text-center">
            {isGenerating ? (
              <FaSpinner size={48} className="mb-2 text-blue-500 animate-spin" />
            ) : (
              <FaFileInvoice size={48} className="mb-2 text-blue-500" />
            )}
          </div>
          <h5 className="mb-3 font-semibold text-gray-800">
            {isGenerating ? 'Generating eTIMS Invoices...' : 'Generate eTIMS Invoices for Selected Tenants?'}
          </h5>
          {!isGenerating && (
            <p className="text-gray-600">
              You are about to generate eTIMS invoices for <span className="font-bold">{selectedTenants.length}</span> selected tenant(s).
              <br />
              This action cannot be undone. Are you sure you want to continue?
            </p>
          )}
        </Modal.Body>
        {!isGenerating && (
          <Modal.Footer className="flex justify-between">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" className="bg-blue-600" onClick={handleConfirmGenerate}>
              Continue
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Failed Invoices Modal */}
      <Modal
        show={showFailedModal}
        onHide={() => setShowFailedModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>eTIMS Generation Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generationSummary && (
            <div className="alert alert-warning">
              <div><strong>Generated:</strong> {generationSummary.createdCount} invoice(s)</div>
              <div><strong>Failed:</strong> {generationSummary.failedCount} invoice(s)</div>
              <div><strong>Already existed:</strong> {generationSummary.skippedCount} invoice(s)</div>
              <div><strong>Processed:</strong> {generationSummary.processedCount} of {generationSummary.requestedCount}</div>
            </div>
          )}
          {failedInvoices.length === 0 ? (
            <p className="text-muted mb-0">No failed invoices.</p>
          ) : (
            <div className="border rounded">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Tenant</th>
                    <th>Unit</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failedInvoices.map((inv) => (
                    <tr key={inv.rowKey || inv.invoice_id || inv.tenant_id}>
                      <td>{inv.invoice_id}</td>
                      <td>{inv.user_name || inv.name}</td>
                      <td>{inv.house_number || inv.unitNumber || "-"}</td>
                      <td>{inv.error || "Not generated"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button variant="secondary" onClick={() => setShowFailedModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleRetryFailed} disabled={isGenerating || failedInvoices.length === 0}>
            {isGenerating ? "Retrying..." : "Retry Failed"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TenantsTable;
